import { useState } from 'react';
import { ExternalLink, Image as ImageIcon } from 'lucide-react';
import { ADD_TO_YARUTA_COPY } from '@/constants/addToYarutaCopy';
import { useUploadSignedUrl } from '@/hooks/useUploadSignedUrl';
import type { UploadKind } from '@/lib/uploadKind';
import { REVIEW_COPY } from '@/pages/review/reviewCopy';

export interface UploadSourcePanelProps {
  /** The upload's `emails` row id. */
  emailId: string;
  kind: UploadKind;
  /**
   * Draw the bordered card (View Source Reference, default). Off inside a
   * Sources row, which is already the card (Figma `.Sources / source`).
   */
  framed?: boolean;
  className?: string;
}

/**
 * A photo or file the parent added, read in place (YAR-556; Figma
 * `View Source Reference` Kind=photo, "Review - View Photo Content"
 * 11694:7620): the photo where an email's body sits, through a short-lived
 * signed URL — no body text, no Open in Gmail.
 */
export function UploadSourcePanel({
  emailId,
  kind,
  framed,
  className,
}: UploadSourcePanelProps) {
  const { url, loading, error, retry } = useUploadSignedUrl(emailId, true);
  return (
    <UploadSourceView
      // A different upload starts from its own placeholder, never the last photo.
      key={emailId}
      kind={kind}
      framed={framed}
      url={url}
      loading={loading}
      error={error}
      onRetry={retry}
      className={className}
    />
  );
}

export interface UploadSourceViewProps {
  kind: UploadKind;
  /** The signed URL, or null while it loads or after a failure. */
  url: string | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  /** See `UploadSourcePanelProps.framed`. Default true. */
  framed?: boolean;
  className?: string;
}

/** The panel's pixels, without the fetch (stories, and anything that already holds the URL). */
export function UploadSourceView({
  kind,
  url,
  loading,
  error,
  onRetry,
  framed = true,
  className = '',
}: UploadSourceViewProps) {
  const alt = ADD_TO_YARUTA_COPY.sourceReferenceSubline(kind);
  // The photo on screen is the last URL that finished loading. A new URL (a
  // renewal) loads off screen and replaces it only once it has, so the
  // placeholder shows until the first photo is decoded and never flashes.
  const [shownUrl, setShownUrl] = useState<string | null>(null);
  // The image itself failed (expired or purged object, dropped connection) —
  // the sign call can succeed and the photo still not arrive.
  const [imageFailed, setImageFailed] = useState(false);
  const showError = error || imageFailed;
  const retry = () => {
    setImageFailed(false);
    setShownUrl(null);
    onRetry();
  };
  const placeholder = (
    <div
      data-testid="upload-source-loading"
      role="img"
      aria-label={ADD_TO_YARUTA_COPY.sourceLoading(kind)}
      aria-busy="true"
      className="flex h-80 w-60 max-w-full items-center justify-center rounded-md bg-surface-strong motion-safe:animate-pulse"
    >
      <ImageIcon
        size={48}
        strokeWidth={1.5}
        aria-hidden="true"
        className="text-icon-default"
      />
    </div>
  );

  let body;
  if (showError) {
    body = (
      <div className="flex w-full flex-col items-center gap-2 text-center">
        <p className="m-0 text-sm text-foreground">
          {ADD_TO_YARUTA_COPY.sourceLoadError(kind)}
        </p>
        <button
          type="button"
          onClick={retry}
          className="inline-flex items-center justify-center px-1.5 py-1 rounded-md border border-border bg-card text-sm font-semibold text-muted-foreground cursor-pointer focus-visible:outline-2 focus-visible:outline-border-focus"
        >
          {REVIEW_COPY.sourcesRetry}
        </button>
      </div>
    );
  } else if (kind === 'file') {
    body =
      loading || !url ? (
        placeholder
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-1.5 w-full rounded-sm border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-surface-secondary transition-colors duration-150 no-underline"
        >
          {ADD_TO_YARUTA_COPY.openFile}
          <ExternalLink size={16} />
        </a>
      );
  } else {
    body = (
      <>
        {shownUrl ? (
          <img
            src={shownUrl}
            alt={alt}
            onError={() => setImageFailed(true)}
            className="block h-auto max-h-80 w-auto max-w-full rounded-md object-contain"
          />
        ) : (
          placeholder
        )}
        {url && url !== shownUrl && (
          // Off screen until decoded; a `hidden` image still loads.
          <img
            src={url}
            alt=""
            hidden
            onLoad={() => setShownUrl(url)}
            // A renewal that won't load keeps the photo already on screen.
            onError={() => {
              if (!shownUrl) setImageFailed(true);
            }}
          />
        )}
      </>
    );
  }

  return (
    <div
      data-testid="upload-source-panel"
      className={[
        'flex flex-col items-center gap-3',
        framed ? 'rounded-lg border border-border-strong bg-card p-5' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {body}
    </div>
  );
}
